import { RouteRecordRaw } from 'vue-router'
import { FeatureList, useLDFlag } from '@/lib/ldClient.ts'

// Lazy loaded components
const PublicLayout = () => import('@/app/layouts/PublicLayout.vue')
const DemoLayout = () => import('@/app/views/DEMO/DemoLayout.vue')
const LandingPageLayout = () => import('@/app/layouts/LandingPageLayout.vue')
const NotFound = () => import('@/app/views/NotFound.vue')

// Helper function to get adviser homepage flag value
const isAdviserHomepageEnabled = () => useLDFlag(FeatureList.ENABLE_ADVISER_HOMEPAGE, false).value
const isDemoModeEnabled = () => useLDFlag(FeatureList.ENABLE_DEMO_MODE, false).value

// Dynamic component resolver that evaluates feature flag fresh on each call
function createDynamicSlots() {
  return {
    headerNav: () => {
      if (isDemoModeEnabled()) return {}
      return isAdviserHomepageEnabled()
          ? import('@/app/components/public/advisers/AdviserHeaderNav.vue')
          : import('@/app/components/public/PublicHeaderNav.vue')
    },
    actions: () => {
      if (isDemoModeEnabled()) return import('@/app/views/DEMO/DemoHeaderActions.vue')
      return isAdviserHomepageEnabled()
        ? import('@/app/components/public/advisers/AdviserHeaderActions.vue')
        : import('@/app/components/public/PublicHeaderActions.vue')
    },
    footerNav: () =>
      isAdviserHomepageEnabled()
        ? import('@/app/components/public/advisers/AdviserFooterNav.vue')
        : import('@/app/components/public/PublicFooterNav.vue'),
  }
}

function getLayoutComponent() {
  const layout = isDemoModeEnabled() ? DemoLayout : PublicLayout
  return layout()
}

function getHomepageComponent() {
  console.log('Determining homepage component to load...', isDemoModeEnabled())
  if (isDemoModeEnabled()) return import('@/app/views/DEMO/DemoPage.vue')
  if (isAdviserHomepageEnabled()) {
    return import('@/app/views/AdviserHomepage.vue')
  }

  const rollingOutRetirement = useLDFlag(FeatureList.ROLLOUT_RETIREMENT_PAGES, false)

  return rollingOutRetirement.value
    ? import('@/app/views/RetirementHome.vue')
    : import('@/app/views/Homepage.vue')
}

export default [
  {
    path: '/',
    component: () => getLayoutComponent(),
    name: 'home',
    children: [
      {
        path: '',
        name: 'homepage',
        components: {
          ...createDynamicSlots(),
          default: () => getHomepageComponent(),
        },
        meta: {
          pageMeta: 'figure out required data and json pattern',
        },
      },
      {
        path: 'retire',
        name: 'landing.retirement',
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/LandingPages/RetirementLP.vue'),
        },
        meta: {
          pageMeta: 'figure out required data and json pattern',
        },
      },
      // Services pages
      {
        path: '/employee-wellbeing',
        name: 'employee-wellbeing',
        meta: {
          type: 'services',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/Services/EmployeeWellbeing.vue'),
        },
      },
      {
        path: '/advice-practices',
        name: 'advice-practices',
        meta: {
          type: 'services',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/Services/AdvicePractices.vue'),
        },
      },
      {
        path: '/super-funds',
        name: 'super-funds',
        meta: {
          type: 'services',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/Services/SuperFunds.vue'),
        },
      },
      // About pages
      {
        path: '/our-team',
        name: 'about-otivo',
        meta: {
          type: 'about',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/About/OurStory.vue'),
        },
      },
      {
        path: '/how-otivo-makes-money',
        name: 'how-otivo-makes-money',
        meta: {
          type: 'about',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/About/HowOtivoMakesMoney.vue'),
        },
      },
      {
        path: '/security',
        name: 'security',
        meta: {
          type: 'about',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/About/Security.vue'),
        },
      },
      {
        path: '/media',
        name: 'media',
        meta: {
          type: 'about',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/About/Media.vue'),
        },
      },
      {
        path: '/contact-us',
        name: 'contact-us',
        meta: {
          type: 'about',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/About/ContactUs.vue'),
        },
      },
      {
        path: '/faq',
        name: 'faq',
        meta: {
          type: 'resources',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/Resources/FrequentlyAskedQuestions.vue'),
        },
      },
      {
        path: '/financial-fitness',
        name: 'financial-fitness',
        meta: {
          type: 'resources',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/Resources/FinancialFitnessTest.vue'),
        },
      },
      {
        path: '/retirement-calculator',
        name: 'retirement-calculator',
        meta: {
          type: 'resources',
        },
        components: {
          ...createDynamicSlots(),
          default: () =>
            import('@/components/OtivoRetirementCalculator/OtivoRetirementCalculator.vue'),
        },
      },
      {
        path: '/super-report',
        name: 'super-report',
        meta: {
          type: 'resources',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/Resources/SuperReport.vue'),
        },
      },
      {
        path: '/download-wellbeing-report',
        name: 'wellbeing-report-form',
        meta: {
          type: 'resources',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/Resources/WellbeingReportForm.vue'),
        },
      },
      {
        path: '/privacy-policy',
        name: 'privacy-policy',
        meta: {
          type: 'legal',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/Resources/SuperReport.vue'),
        },
      },
      {
        path: '/content/privacy',
        alias: '/privacy-policy',
        name: 'privacy-policy',
        meta: {
          type: 'legal',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/Legal/PrivacyPolicy.vue'),
        },
      },
      {
        path: '/content/termsAndConditions',
        alias: '/terms-and-conditions',
        name: 'terms-and-conditions',
        meta: {
          type: 'legal',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/Legal/TermsConditions.vue'),
        },
      },
      {
        path: ':pathMatch(.*)*',
        name: 'not-found',
        components: {
          ...createDynamicSlots(),
          default: NotFound,
        },
      },
    ],
  },
  {
    path: '/learn',
    component: PublicLayout,
    children: [
      {
        path: '',
        name: 'Learning hub',
        meta: {
          type: 'resources',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/LearningHub/LearningHub.vue'),
        },
      },
      {
        path: 'search',
        name: 'Search',
        meta: {
          type: 'resources',
        },
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/LearningHub/SearchPage.vue'),
        },
      },
      {
        path: ':slug+',
        name: 'Slug',
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/views/LearningHub/Article.vue'),
        },
      },
    ],
  },
  {
    path: '/select-plan',
    component: PublicLayout,
    children: [
      {
        path: '',
        name: 'select-plan',
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/components/pricing/PersonalPricing.vue'),
        },
      },
    ],
  },
  {
    path: '/personal-pricing',
    component: PublicLayout,
    children: [
      {
        path: '',
        name: 'personal-pricing',
        components: {
          ...createDynamicSlots(),
          default: () => import('@/app/components/pricing/PersonalPricing.vue'),
        },
      },
    ],
  },
  {
    path: '/plans',
    component: PublicLayout,
    children: [
      {
        path: '',
        name: 'plans',
        components: {
          default: () => import('@/components/FreemiumOnboarding/PlanPage.vue'),
        },
      },
    ],
  },
  {
    // landingpages this is separate to the main public layout
    path: '/retire-with-otivo',
    component: LandingPageLayout,
    children: [
      {
        path: '',
        name: 'landing.retire-with-otivo',
        components: {
          default: () => import('@/app/views/LandingPages/RetireWithOtivoLP.vue'),
        },
        meta: {
          layout: 'landing',
          seo: {
            title: 'Otivo. Be better off. - Retire with Otivo!',
            description:
              'Otivo makes retirement planning simple and affordable for Australians. Get licensed financial advice, personalised retirement plans, and strategies to grow your super—all in minutes, for just $119 a year. Start planning today and enjoy more freedom in retirement.',
            keywords:
              'financial advice, super, investments, insurance, debt management, retirement planning, financial planning australia',
            noindex: true,
            nofollow: true,
          },
        },
      },
    ],
  },
  {
    path: '/get-started',
    component: PublicLayout,
    children: [
      {
        path: '',
        name: 'get-started',
        components: {
          default: () => import('@/app/views/GetStarted.vue'),
        },
      },
    ],
  },
] as RouteRecordRaw[]
